import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DoorOpen, ImagePlus, Loader2, UserPlus } from "lucide-react";
import { DoorGuestAdminRow } from "./DoorGuestAdminRow";
import { DuplicateNotice } from "./DuplicateNotice";
import { api, formatApiError } from "../lib/api";
import { DEFAULT_LABEL, insertSorted, labelOf, newRequestId, replaceGuest } from "../lib/doorGuests";

const input = "h-11 min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-700 sm:text-sm";
const EMPTY = { identifier: "", display_name: "", first_name: "", last_name: "" };

const LabelSetting = ({ eventId, value, onSaved }) => {
  const [text, setText] = useState(value);
  const save = async () => {
    try {
      const { data } = await api.put(`/events/${eventId}/guest-identifier-label`, { guest_identifier_label: text });
      onSaved(data.guest_identifier_label);
      toast.success("Label saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <label htmlFor="door-label" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Identifier label</label>
      <input id="door-label" data-testid="door-label-input" className={`${input} sm:w-56`} maxLength={40} placeholder={DEFAULT_LABEL} value={text} onChange={(e) => setText(e.target.value)} />
      <button type="button" data-testid="door-label-save" disabled={text.trim() === value} onClick={save} className="h-11 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Save label</button>
    </div>
  );
};

export const DoorGuestSection = ({ eventId, reloadKey, onChanged }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const requestId = useRef(newRequestId());

  useEffect(() => {
    api.get(`/events/${eventId}/door-guests`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [eventId, reloadKey]);

  if (!data) return null;
  const label = labelOf(data.guest_identifier_label);

  const add = async (e) => {
    e.preventDefault();
    if (!form.identifier.trim()) return toast.error(`${label} is required`);
    setBusy(true);
    try {
      const { data: r } = await api.post(`/events/${eventId}/door-guests`, { ...form, request_id: requestId.current });
      setData((d) => ({ ...d, guests: d.guests.some((g) => g.id === r.guest.id) ? d.guests : insertSorted(d.guests, r.guest), counts: r.counts }));
      setForm(EMPTY);
      onChanged();
      requestId.current = newRequestId();
      setNotice(r.possible_duplicates.length ? { guest: r.guest, duplicates: r.possible_duplicates } : null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const save = async (guest, changes) => {
    try {
      const { data: r } = await api.patch(`/events/${eventId}/door-guests/${guest.id}`, changes);
      setData((d) => ({ ...d, guests: replaceGuest(d.guests, r.guest) }));
      onChanged();
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    }
  };

  const remove = async (guest) => {
    try {
      const { data: counts } = await api.delete(`/events/${eventId}/door-guests/${guest.id}`);
      setData((d) => ({ ...d, guests: d.guests.filter((g) => g.id !== guest.id), counts }));
      if (notice?.guest.id === guest.id) setNotice(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section data-testid="door-guest-section" className="mt-12 rounded-3xl border border-slate-200 bg-white p-5 sm:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700">Door list</p>
          <h2 className="font-display mt-1 text-2xl font-extrabold tracking-tight text-slate-900">Guest list</h2>
          <p className="mt-1 text-sm text-slate-500">
            <span data-testid="door-admin-total">{data.counts.total}</span> guests · <span data-testid="door-admin-checked">{data.counts.checked_in}</span> checked in. Counted as Yes in your responses and shown by name in Who's coming (if your guest list is visible).
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Link to={`/events/${eventId}/import`} data-testid="door-import-link" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-slate-200 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50"><ImagePlus className="h-4 w-4" /> Import from screenshots</Link>
          <Link to={`/events/${eventId}/door`} data-testid="door-checkin-link" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-5 text-sm font-semibold text-white hover:bg-blue-600"><DoorOpen className="h-4 w-4" /> Door check-in</Link>
        </div>
      </div>
      <div className="mt-6"><LabelSetting eventId={eventId} value={data.guest_identifier_label} onSaved={(v) => setData((d) => ({ ...d, guest_identifier_label: v }))} /></div>
      <form onSubmit={add} noValidate data-testid="door-admin-add-form" className="mt-6 flex flex-col gap-2 sm:flex-row">
        <input data-testid="door-admin-identifier-input" className={`${input} sm:flex-[2]`} maxLength={200} placeholder={`${label} *`} value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} />
        <input data-testid="door-admin-display-input" className={`${input} sm:flex-[2]`} maxLength={200} placeholder="Display name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
        <input data-testid="door-admin-first-input" className={`${input} sm:flex-1`} maxLength={80} placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        <input data-testid="door-admin-last-input" className={`${input} sm:flex-1`} maxLength={80} placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        <button type="submit" data-testid="door-admin-add-btn" disabled={busy} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 text-sm font-semibold text-white disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add guest
        </button>
      </form>
      {notice && <div className="mt-3"><DuplicateNotice notice={notice} label={label} onKeep={() => setNotice(null)} onUndo={() => remove(notice.guest)} /></div>}
      {data.guests.length === 0 ? (
        <p data-testid="door-admin-empty" className="mt-6 rounded-xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">No guests yet. Add the people you expect at the door.</p>
      ) : (
        <ul data-testid="door-admin-list" className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
          {data.guests.map((g) => <DoorGuestAdminRow key={g.id} guest={g} onSave={save} onDelete={remove} />)}
        </ul>
      )}
    </section>
  );
};
